// Browser-local rendering for structured scheduled instants, plus the browser
// timezone for forms that submit local times.  Server text remains a readable fallback.
(function () {
    let appointmentListNavigating = false;

    function browserTimezone() {
        try {
            return Intl.DateTimeFormat().resolvedOptions().timeZone || '';
        } catch (_error) {
            return '';
        }
    }

    function timezoneLabel() {
        try {
            const parts = Intl.DateTimeFormat(undefined, { timeZoneName: 'short' }).formatToParts(new Date());
            return (parts.find((part) => part.type === 'timeZoneName') || {}).value || browserTimezone();
        } catch (_error) {
            return browserTimezone();
        }
    }

    function populate(root) {
        const scope = root && root.querySelectorAll ? root : document;
        const zone = browserTimezone();
        if (zone) {
            scope.querySelectorAll('[data-browser-timezone]').forEach((input) => { input.value = zone; });
            // Persist it so the server can bucket appointment-list days without
            // putting browser state in the URL.
            if (window._cc && _cc.setCookie) _cc.setCookie('browser_timezone', zone);
        }
        const label = timezoneLabel();
        if (label) {
            scope.querySelectorAll('[data-browser-timezone-label]').forEach((element) => { element.textContent = label; });
        }
        scope.querySelectorAll('time[data-browser-local-datetime]').forEach((element) => {
            const date = new Date(element.getAttribute('datetime'));
            if (Number.isNaN(date.getTime())) return;
            try {
                if (element.hasAttribute('data-browser-local-end')) {
                    const end = new Date(element.getAttribute('data-browser-local-end'));
                    if (Number.isNaN(end.getTime()) || end < date) return;
                    const dateOptions = { weekday: 'long', month: 'long', day: 'numeric' };
                    const timeOptions = {
                        hour: 'numeric', minute: '2-digit', hour12: true, timeZoneName: 'short',
                    };
                    // Overnight appointments need both dates; don't imply the end is earlier that day.
                    element.textContent = date.toDateString() === end.toDateString()
                        ? `${new Intl.DateTimeFormat(undefined, dateOptions).format(date)} · ${
                            new Intl.DateTimeFormat(undefined, timeOptions).formatRange(date, end)}`
                        : new Intl.DateTimeFormat(undefined, {
                            ...dateOptions, ...timeOptions, year: 'numeric',
                        }).formatRange(date, end);
                    return;
                }
                // `data-browser-local-numeric` writes the date in digits with the year, e.g.
                // "11/03/2026, 6:17 AM", for log entries that may be years old.
                if (element.hasAttribute('data-browser-local-numeric')) {
                    element.textContent = new Intl.DateTimeFormat(undefined, {
                        year: 'numeric', month: '2-digit', day: '2-digit',
                        hour: 'numeric', minute: '2-digit', hour12: true,
                    }).format(date);
                    return;
                }
                // `data-browser-local-weekday` names the day in full; its "short" value abbreviates it.
                const weekdayMode = element.getAttribute('data-browser-local-weekday');
                element.textContent = new Intl.DateTimeFormat(undefined, {
                    weekday: weekdayMode === null ? undefined : (weekdayMode === 'short' ? 'short' : 'long'),
                    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true,
                    timeZoneName: 'short',
                }).format(date);
            } catch (_error) {
                // Keep the server-rendered fallback when Intl cannot format it.
            }
        });
    }

    function appointmentListStates(root) {
        const scope = root && root.querySelectorAll ? root : document;
        const states = [];
        if (scope.matches && scope.matches('[data-browser-local-datetime-list]')) states.push(scope);
        scope.querySelectorAll('[data-browser-local-datetime-list]').forEach((state) => states.push(state));
        states.forEach((state) => {
            if (appointmentListNavigating) return;
            const zone = browserTimezone();
            if (!zone) {
                const error = state.querySelector('[data-browser-local-datetime-list-error]');
                if (error) error.textContent = 'Your browser did not provide a timezone. Set one and refresh this page.';
                return;
            }
            const storedZone = window._cc && _cc.getCookie ? _cc.getCookie('browser_timezone') : '';
            if (storedZone !== zone) {
                const error = state.querySelector('[data-browser-local-datetime-list-error]');
                if (error) error.textContent = 'Your browser timezone could not be saved. Enable cookies and refresh this page.';
                return;
            }
            appointmentListNavigating = true;
            const url = new URL(window.location.href);
            url.searchParams.delete('browser_timezone');
            url.searchParams.delete('page');
            window.location.replace(url.pathname + url.search + url.hash);
        });
    }

    function onReady(event) {
        const root = event && event.target;
        populate(root);
        appointmentListStates(root);
    }
    // Swaps done outside HTMX's lifecycle (WebSocket fragment refreshes, the
    // manual image-card replace) fire none of the events below, so a re-rendered
    // <time data-browser-local-datetime> would keep the server-side fallback
    // zone. Expose the pass for those call sites — same shape as
    // `_cc.applyClampList`, which is re-applied at the same places.
    window._cc = window._cc || {};
    window._cc.applyBrowserLocalDatetime = populate;
    document.addEventListener('DOMContentLoaded', () => onReady({ target: document }));
    // A stage form can be portalled or swapped after the lifecycle event that
    // normally initializes it. Fill the zone again at the last responsible
    // moment so HTMX never submits an otherwise valid schedule with a blank
    // hidden timezone.
    document.body.addEventListener('submit', (event) => {
        const form = event.target;
        if (!form || !form.querySelector) return;
        const input = form.querySelector('[data-browser-timezone]');
        if (input) input.value = browserTimezone();
    }, true);
    document.body.addEventListener('sheet:ready', onReady);
    document.body.addEventListener('htmx:afterSettle', onReady);
    document.body.addEventListener('htmx:load', onReady);
}());
